import { useLocalStorage } from "@scribenote/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { useNoteUsage } from "./use-note-usage";

const preferencesContext = createContext();

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const usePreferences = () => {
  return useContext(preferencesContext);
};

// Hook
export function useProvidePreferences() {
  // Use our useLocalStorage hook to persist state through a page refresh.
  // Read the recipe for this hook to learn more: usehooks.com/useLocalStorage
  const { totalNotesCreated } = useNoteUsage();
  const { releaseWebEnableFtueTour, releaseWebOnboardingMilestones } =
    useFlags();

  const [firstTimeTourDisabled, setFirstTimeTourDisabled] =
    useLocalStorage("first-time-tour-disabled", null);

  const [reviewNoteTourSeen, setReviewNoteTourSeen] = useLocalStorage(
    "review-note-tour-seen",
    true,
  );

  const [exportNoteTourSeen, setExportNoteTourSeen] = useLocalStorage(
    "export-note-tour-seen",
    true,
  );

  const [isFindAndReplaceEnabled, setIsFindAndReplaceEnabled] =
    useState(localStorage.getItem("find-replace-enabled") === "true");

  useEffect(() => {
    // When milestone checklist is active, the tour should only start via
    // the "Show Me How" button — never auto-start.
    if (releaseWebOnboardingMilestones) return;

    if (firstTimeTourDisabled !== null) return;
    if (totalNotesCreated === undefined) return;

    // Under new onboarding: only auto-start FTUE for brand-new users (0 notes)
    // who just came through the welcome screen.
    // Legacy behavior: auto-start for users with < 10 notes.
    const threshold = releaseWebEnableFtueTour ? 0 : 10;

    if (totalNotesCreated <= threshold) {
      setFirstTimeTourDisabled(false);
    }
  }, [
    firstTimeTourDisabled,
    totalNotesCreated,
    setFirstTimeTourDisabled,
    releaseWebEnableFtueTour,
    releaseWebOnboardingMilestones,
  ]);

  useEffect(() => {
    localStorage.setItem(
      "find-replace-enabled",
      isFindAndReplaceEnabled,
    );
  }, [isFindAndReplaceEnabled]);

  // Return enabled  and setter
  return {
    firstTimeTourDisabled,
    setFirstTimeTourDisabled,
    reviewNoteTourSeen,
    setReviewNoteTourSeen,
    exportNoteTourSeen,
    setExportNoteTourSeen,
    isFindAndReplaceEnabled,
    setIsFindAndReplaceEnabled,
  };
}

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function PreferencesProvider({ children }) {
  const preferences = useProvidePreferences();

  return (
    <preferencesContext.Provider value={preferences}>
      {children}
    </preferencesContext.Provider>
  );
}
